import React, { useState, useEffect } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Button,
  MenuItem,
  Select,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormLabel, Paper
} from "@mui/material";
import { getAllOrders, updateOrderStatus } from "../../api/OrdersAPI";
import { toast } from "react-toastify";

const validStatuses = [
  "not processed",
  "cash on delivery",
  "processing",
  "dispatched",
  "cancelled",
  "delivered",
];

const OrderPage = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [orderStatus, setOrderStatus] = useState("");
  const [orderDetails, setOrderDetails] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    const fetchedOrders = await getAllOrders();
    const sortedData = fetchedOrders.sort((a, b) => b.createdAt - a.createdAt);
    setOrders(sortedData);
    setFilteredOrders(sortedData);
  };

  const handleView = (order) => {
    setOrderDetails(order);
    setShowModal(true);
  };

  const handleEdit = (order) => {
    setOrderDetails(order);
    setOrderStatus(order.orderStatus);
    setShowEditModal(true);
  };

  const handleStatusChange = (event) => {
    setOrderStatus(event.target.value);
  };

  const handleSaveStatus = async () => {
    try {
      setLoading(true)
      if (orderDetails && orderDetails._id) {
        const updatedOrder = await updateOrderStatus(orderDetails._id, orderStatus);

        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order._id === updatedOrder._id ? updatedOrder : order
          )
        );
        setFilteredOrders((prevOrders) =>
          prevOrders.map((order) =>
            order._id === updatedOrder._id ? updatedOrder : order
          )
        );
        await fetchOrders()
        toast.success("Order status updated successfully!");
      }
      setLoading(false)
      setShowEditModal(false);
    } catch (error) {
      toast.error("Failed to update order status.");
    }
  };

  const handleFilterChange = (event) => {
    const selectedStatus = event.target.value;
    setFilterStatus(selectedStatus);

    if (selectedStatus === "all") {
      setFilteredOrders(orders);
    } else {
      const filtered = orders.filter((order) => order.orderStatus === selectedStatus);
      setFilteredOrders(filtered);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const headers = [
    "ID",
    "Product",
    "Total After Discount",
    "Order By",
    "Shipping Address",
    "Is Paid",
    "Paid At",
    "Is Delivered",
    "Order Status",
  ];

  function getWidth(header) {
    if (header.toLowerCase() === 'product') {
      return 600
    } else if (header.toLowerCase() === 'id') {
      return 200
    } else {
      return 150
    }
  }

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Order Details
      </Typography>

      {/* Filter Dropdown */}
      <FormLabel>{'Status'}</FormLabel>
      <Select
        value={filterStatus}
        onChange={handleFilterChange}
        displayEmpty
        fullWidth
        sx={{ marginBottom: 2 }}
      >
        <MenuItem value="all">All</MenuItem>
        {validStatuses.map((status, index) => (
          <MenuItem key={index} value={status}>
            {status}
          </MenuItem>
        ))}
      </Select>

      {/* Table Container */}
      <TableContainer component={Paper} style={{ maxHeight: "500px" }}>
        <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              {headers.map((header, index) => (
                <TableCell width={getWidth(header)} key={index}>{header}</TableCell>
              ))}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((order) => {
                const product = order.products.map(product =>
                  `Title: ${product?.title || 'Name not found'}, Count: ${product?.count || 'Count not found'}, Price: ${product?.price || 'Price not found'}`
                ).join('\n')
                return (
                  <TableRow key={order._id}>
                    <TableCell>{order._id}</TableCell>
                    <TableCell>{product}</TableCell>
                    <TableCell>{order.totalAfterDiscount}</TableCell>
                    <TableCell>{order.orderby?.name + ' ' + order.orderby?.userId}</TableCell>
                    <TableCell>
                      {order.shippingAddress?.addressLine1}, {order.shippingAddress?.city},{" "}
                      {order.shippingAddress?.state} - {order.shippingAddress?.postalCode}
                    </TableCell>
                    <TableCell>{order.isPaid ? "Yes" : "No"}</TableCell>
                    <TableCell>{new Date(order.paidAt).toLocaleString() || "N/A"}</TableCell>
                    <TableCell>{order.isDelivered ? "Yes" : "No"}</TableCell>
                    <TableCell>{order.orderStatus}</TableCell>
                    <TableCell>
                      <Button onClick={() => handleView(order)}>View</Button>
                      <Button
                        onClick={() => handleEdit(order)}
                        sx={{ marginLeft: 1 }}
                        variant="outlined"
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredOrders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* View Modal */}
      <Dialog open={showModal} onClose={() => setShowModal(false)} fullWidth>
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent>
          <pre>{JSON.stringify(orderDetails, null, 2)}</pre>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} fullWidth>
        <DialogTitle>Edit Order Status</DialogTitle>
        <DialogContent>
          <Select
            value={orderStatus}
            onChange={handleStatusChange}
            fullWidth
            displayEmpty
          >
            {validStatuses.map((status, index) => (
              <MenuItem key={index} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button disabled={loading} onClick={() => setShowEditModal(false)}>Cancel</Button>
          <Button disabled={loading} onClick={handleSaveStatus} variant="contained">
            {loading ? 'Please Wait' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </div >
  );
};

export default OrderPage;
